import type { AdsConfiguration } from '../../config/integration.mjs';
export interface AdConsent { ads: boolean; networkIds: string[]; tcf?: string; gpp?: string }
export interface AdController { setConsent: (consent: AdConsent) => void; destroy: () => void }
export interface AdSdk {
  version: string;
  mount: (config: AdsConfiguration & { element: HTMLElement; consent?: AdConsent }) => AdController;
  setConsent: (consent: AdConsent) => void;
}
declare global {
  const __ADS_CONFIG__: AdsConfiguration;
  interface Window {
    PicPlayerAds?: AdSdk;
    ConstructCanvasAds?: { setConsent: (consent: AdConsent) => void; openPreferences: () => void; useConsentPlatform: () => void };
    ConstructCanvasConsent?: { openPreferences: () => void };
  }
}
export const ADS_CONFIG = __ADS_CONFIG__;
let sdkPromise: Promise<AdSdk> | undefined;
let consent: AdConsent = { ads: false, networkIds: [] };
let locallyBlocked = false;
const BLOCK_KEY = 'constructcanvas:ads-contextual-only:v1';
try { locallyBlocked = localStorage.getItem(BLOCK_KEY) === '1'; } catch { /* Privacy mode. */ }

export function currentConsent(): AdConsent {
  return locallyBlocked ? { ads: false, networkIds: [] } : consent;
}
/** The CMP must call this only after obtaining valid, network-specific permission. */
export function setAdConsent(value: AdConsent): void {
  consent = { ads: value?.ads === true, networkIds: Array.isArray(value?.networkIds) ? value.networkIds.filter((id) => typeof id === 'string') : [], tcf: value?.tcf, gpp: value?.gpp };
  window.PicPlayerAds?.setConsent(currentConsent());
  document.dispatchEvent(new CustomEvent('constructcanvas:consent-updated'));
}
export function contextualOnly(): void {
  locallyBlocked = true;
  try { localStorage.setItem(BLOCK_KEY, '1'); } catch { /* Still enforced this session. */ }
  setAdConsent({ ads: false, networkIds: [] });
}
export function useConsentPlatform(): void {
  locallyBlocked = false;
  try { localStorage.removeItem(BLOCK_KEY); } catch { /* Still enforced this session. */ }
  // Never resurrect a stale grant: the CMP must return fresh permission.
  setAdConsent({ ads: false, networkIds: [] });
  window.ConstructCanvasConsent?.openPreferences();
}
export function openAdPreferences(): void {
  const event = new CustomEvent('picplayer:ad-preferences', { bubbles: true, cancelable: true, composed: true });
  document.dispatchEvent(event);
}
export function loadAdSdk(): Promise<AdSdk> {
  if (window.PicPlayerAds) return Promise.resolve(window.PicPlayerAds);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<AdSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/ads/rail.js'; script.async = true; script.dataset.constructcanvasSdk = 'true';
    const timer = window.setTimeout(() => fail(), 6000);
    function fail() { clearTimeout(timer); script.remove(); sdkPromise = undefined; reject(new Error('The shared ad SDK could not load.')); }
    script.onerror = fail;
    script.onload = () => { clearTimeout(timer); if (window.PicPlayerAds) resolve(window.PicPlayerAds); else fail(); };
    document.head.append(script);
  });
  return sdkPromise;
}

// Grants are intentionally never persisted. A stale saved grant is not consent.
// Only an opt-out is stored, and propagated across tabs.
window.addEventListener('storage', (event) => {
  if (event.key === BLOCK_KEY || event.key === null) {
    locallyBlocked = event.newValue === '1';
    setAdConsent({ ads: false, networkIds: [] });
  }
});
window.ConstructCanvasAds = { setConsent: setAdConsent, openPreferences: openAdPreferences, useConsentPlatform };
